// Barcodes an der Kasse — zwei Wege:
//
//   Handscanner   USB oder Bluetooth. Er „tippt" den Code sehr schnell und
//                 schickt Enter hinterher. Steht der Cursor im Suchfeld,
//                 erledigt das Suchfeld den Rest; sonst fängt useScannerTastatur
//                 die schnelle Ziffernfolge ab — man muss nicht erst ins Feld
//                 klicken.
//   Kamera        über BarcodeDetector (Chrome/Edge, Android). Safari und die
//                 iOS-App kennen ihn nicht — dort erscheint der Knopf nicht.
import { useEffect, useRef, useState } from 'react';
import { Fenster } from '../../../gemeinsam/komponenten/Fenster';
import { Hinweis } from '../../../gemeinsam/komponenten/Basis';
import { useSprache } from '../../../gemeinsam/lib/i18n';

type Erkannt = { rawValue: string };
type Detektor = { detect: (q: HTMLVideoElement) => Promise<Erkannt[]> };
type DetektorKlasse = new (o: { formats: string[] }) => Detektor;

const klasse = () => (globalThis as unknown as { BarcodeDetector?: DetektorKlasse }).BarcodeDetector;

export const kameraVerfuegbar = () => !!klasse() && !!navigator.mediaDevices?.getUserMedia;

/** Schnelle Tastenfolge + Enter außerhalb von Eingabefeldern = Scan. */
export function useScannerTastatur(beiCode: (code: string) => void, aktiv = true) {
  const rueckruf = useRef(beiCode);
  rueckruf.current = beiCode;
  useEffect(() => {
    if (!aktiv) return;
    let puffer = '';
    let zuletzt = 0;
    const taste = (e: KeyboardEvent) => {
      const ziel = e.target as HTMLElement | null;
      if (ziel && (ziel.tagName === 'INPUT' || ziel.tagName === 'TEXTAREA' || ziel.tagName === 'SELECT' || ziel.isContentEditable)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const jetzt = Date.now();
      // Menschen tippen langsamer als ~50 ms je Zeichen; Scanner schneller.
      if (jetzt - zuletzt > 60) puffer = '';
      zuletzt = jetzt;
      if (e.key === 'Enter') {
        if (puffer.length >= 4) { e.preventDefault(); rueckruf.current(puffer); }
        puffer = '';
      } else if (e.key.length === 1) {
        puffer += e.key;
      }
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [aktiv]);
}

export function KameraScanner({ beiCode, beiSchliessen }: { beiCode: (code: string) => void; beiSchliessen: () => void }) {
  const { t } = useSprache();
  const video = useRef<HTMLVideoElement>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let strom: MediaStream | null = null;
    let laeuft = true;
    let zeitgeber: ReturnType<typeof setTimeout> | null = null;
    const K = klasse();
    if (!K) return;
    const detektor = new K({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code'] });

    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(async s => {
        strom = s;
        if (!video.current || !laeuft) return;
        video.current.srcObject = s;
        await video.current.play();
        const runde = async () => {
          if (!laeuft || !video.current) return;
          try {
            const treffer = await detektor.detect(video.current);
            if (treffer[0]?.rawValue) { laeuft = false; beiCode(treffer[0].rawValue); return; }
          } catch { /* nächstes Bild */ }
          zeitgeber = setTimeout(runde, 250);
        };
        void runde();
      })
      .catch(() => setFehler(t('kasse.kameraFehler')));

    return () => {
      laeuft = false;
      if (zeitgeber) clearTimeout(zeitgeber);
      strom?.getTracks().forEach(s => s.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fenster titel={t('kasse.scannen')} beiSchliessen={beiSchliessen} breite="sm:max-w-md">
      <div className="space-y-3 p-4">
        {fehler ? <Hinweis art="warnung">{fehler}</Hinweis> : (
          <div className="relative overflow-hidden rounded-xl bg-black">
            <video ref={video} playsInline muted className="aspect-[4/3] w-full object-cover" />
            <div aria-hidden className="pointer-events-none absolute inset-x-8 top-1/2 h-0.5 -translate-y-1/2 bg-red-500/80 shadow-[0_0_12px_rgba(239,68,68,0.8)]" />
          </div>
        )}
        <p className="text-center text-sm text-slate-500 dark:text-slate-400">{t('kasse.scannenHinweis')}</p>
      </div>
    </Fenster>
  );
}
